class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (
        !sectionElement ||
        !parsedState.sections ||
        !parsedState.sections[section.id]
      ) {
        return;
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const selectedElement =
      parsedDocument.querySelector(selector);

    return selectedElement
      ? selectedElement.innerHTML
      : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}

/**
 * Signature Required Upon Delivery add-on
 */
class SignatureRequiredAddon extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector(
      '[data-signature-toggle]'
    );

    this.errorElement = this.querySelector(
      '[data-signature-error]'
    );

    if (
      !this.toggle ||
      this.dataset.signatureBound === 'true'
    ) {
      return;
    }

    this.dataset.signatureBound = 'true';

    this.handleToggleBound =
      this.handleToggle.bind(this);

    this.toggle.addEventListener(
      'change',
      this.handleToggleBound
    );

    /*
     * If all normal products are removed but the signature
     * add-on remains, remove the add-on automatically.
     */
    const otherItemCount = parseInt(
      this.dataset.otherItemsCount || '0',
      10
    );

    if (
      this.toggle.checked &&
      otherItemCount <= 0 &&
      this.dataset.lineKey
    ) {
      window.requestAnimationFrame(() => {
        this.updateAddon(false, true);
      });
    }
  }

  disconnectedCallback() {
    if (
      this.toggle &&
      this.handleToggleBound
    ) {
      this.toggle.removeEventListener(
        'change',
        this.handleToggleBound
      );
    }
  }

  async handleToggle() {
    const shouldAdd = this.toggle.checked;

    await this.updateAddon(shouldAdd, false);
  }

  async updateAddon(shouldAdd, silent = false) {
    if (!this.toggle) return;

    if (
      shouldAdd &&
      !this.dataset.variantId
    ) {
      this.toggle.checked = false;
      this.showError(
        'The signature add-on product has not been configured.'
      );
      return;
    }

    if (
      !shouldAdd &&
      !this.dataset.lineKey
    ) {
      return;
    }

    this.setLoading(true);
    this.clearError();

    try {
      const sections = [
        'cart-drawer',
        'cart-icon-bubble',
      ];

      const root =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      const addUrl =
        window.routes &&
        window.routes.cart_add_url
          ? window.routes.cart_add_url
          : `${root}cart/add.js`;

      const changeUrl =
        window.routes &&
        window.routes.cart_change_url
          ? window.routes.cart_change_url
          : `${root}cart/change.js`;

      const requestBody = shouldAdd
        ? {
            items: [
              {
                id: this.dataset.variantId,
                quantity: 1,
                properties: {
                  _signature_required: 'Yes',
                  _signature_service_code:
                    'SIGNATURE_REQUIRED',
                },
              },
            ],
            sections,
            sections_url:
              window.location.pathname +
              window.location.search,
          }
        : {
            id: this.dataset.lineKey,
            quantity: 0,
            sections,
            sections_url:
              window.location.pathname +
              window.location.search,
          };

      const response = await fetch(
        shouldAdd ? addUrl : changeUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.description ||
          responseData.message ||
          'Unable to update the cart.'
        );
      }

      const cartDrawer =
        document.querySelector('cart-drawer');

      if (
        cartDrawer &&
        responseData.sections &&
        typeof cartDrawer.renderContents ===
          'function'
      ) {
        cartDrawer.renderContents(responseData);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error(
        'Signature add-on error:',
        error
      );

      if (!silent) {
        this.toggle.checked = !shouldAdd;

        this.showError(
          error.message ||
          'Unable to update the signature option. Please try again.'
        );
      }

      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.classList.toggle(
      'is-loading',
      isLoading
    );

    this.setAttribute(
      'aria-busy',
      isLoading ? 'true' : 'false'
    );

    if (this.toggle) {
      this.toggle.disabled = isLoading;
    }
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  clearError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = '';
    this.errorElement.hidden = true;
  }
}

if (
  !customElements.get(
    'signature-required-addon'
  )
) {
  customElements.define(
    'signature-required-addon',
    SignatureRequiredAddon
  );
}